import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { ScrollView } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import {
  ChevronRightIcon,
  PlusIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { SquareCard } from "@whoosh/features/src/Square";

import { SelectedPaymentMethod } from "./SelectedPaymentMethod";
import { SelectedPaymentMethodFragment$key } from "./SelectedPaymentMethod/__generated__/SelectedPaymentMethodFragment.graphql";
import { PaymentMethodsQuery } from "./__generated__/PaymentMethodsQuery.graphql";

type PaymentMethodsProps = {
  queryRef: PreloadedQuery<PaymentMethodsQuery>;
  setIsAddACardModalOpen: (isOpen: boolean) => void;
  setIsPaymentMethodsModalOpen: (isOpen: boolean) => void;
  refreshQuery: () => void;
};

export const PaymentMethods = ({
  queryRef,
  setIsAddACardModalOpen,
  setIsPaymentMethodsModalOpen,
  refreshQuery,
}: PaymentMethodsProps) => {
  const data = usePreloadedQuery(PaymentMethodsQueryString, queryRef);
  const { runAfterIdle } = useModalTransition();

  const [selectedPaymentMethodRef, setSelectedPaymentMethodRef] =
    useState<SelectedPaymentMethodFragment$key | null>(null);

  const openAddACardModal = () => {
    setIsPaymentMethodsModalOpen(false);
    runAfterIdle(() => {
      setIsAddACardModalOpen(true);
    });
  };

  const userCards =
    data.session?.user?.squareCards?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) ?? [];

  const profiles =
    data.session?.user?.memberProfiles?.edges?.flatMap((e) =>
      e?.node?.member ? [e.node.member] : []
    ) || [];

  const clubCards = profiles.map((p) => ({
    clubName: p?.club?.name,
    cards:
      p?.memberCards?.edges?.flatMap((edge) =>
        edge?.node ? [edge.node] : []
      ) || [],
  }));

  const hasClubCards = clubCards.some((c) => c.cards.length > 0);

  return (
    <Box px={6} pb={6} minHeight={{ md: 300 }} flex={1}>
      <Text
        pt={8}
        pb={2}
        variant="mainTitle2"
        color="text-primary"
        testID="payment-methods-header"
      >
        {t`Payment Methods`}
      </Text>
      <Box my={3} flex={1}>
        <ScrollView>
          <VStack gap={8}>
            <VStack gap={4}>
              {hasClubCards ? (
                <VStack gap={1}>
                  <Text variant="mainSmallSemiBold">{t`Account Cards`}</Text>
                  <Text
                    variant="mainSmallMedium"
                    color="text-secondary"
                  >{t`Added by you - usable across all clubs`}</Text>
                </VStack>
              ) : null}
              <VStack gap={2} testID="square-card-container">
                {userCards.map((card, index) => (
                  <SquareCard
                    key={index}
                    fragmentRef={card ?? null}
                    onPress={() => setSelectedPaymentMethodRef(card)}
                    rightIcon={<ChevronRightIcon />}
                  />
                ))}
                <Pressable
                  onPress={openAddACardModal}
                  testID="payment-methods-add-a-card-button"
                >
                  <HStack alignItems="center" gap={3}>
                    <Box
                      width={40}
                      height={26}
                      borderRadius="2"
                      backgroundColor="surface-secondary"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <PlusIcon />
                    </Box>
                    <Text
                      variant="mainSmallSemiBold"
                      flexGrow={1}
                    >{t`Add Credit or Debit Card`}</Text>
                    <ChevronRightIcon />
                  </HStack>
                </Pressable>
              </VStack>
            </VStack>
            {hasClubCards ? (
              <VStack gap={4}>
                <VStack gap={1}>
                  <Text variant="mainSmallSemiBold">{t`Club Specific Cards`}</Text>
                  <Text
                    variant="mainSmallMedium"
                    color="text-secondary"
                  >{t`These cards were added by your club and can only be used at the club they were added to.`}</Text>
                </VStack>
                {clubCards.map(({ clubName, cards }) => (
                  <VStack gap={2} key={clubName}>
                    <Text variant="mainTiny">{clubName}</Text>
                    {cards.map((card, cardIndex) => (
                      <SquareCard
                        key={`${clubName}-card-${cardIndex}`}
                        fragmentRef={card ?? null}
                        onPress={() => setSelectedPaymentMethodRef(card)}
                        rightIcon={<ChevronRightIcon />}
                      />
                    ))}
                  </VStack>
                ))}
              </VStack>
            ) : null}
          </VStack>
        </ScrollView>
      </Box>
      {selectedPaymentMethodRef ? (
        <SelectedPaymentMethod
          onClose={() => setSelectedPaymentMethodRef(null)}
          fragmentRef={selectedPaymentMethodRef}
          refreshQuery={refreshQuery}
        />
      ) : null}
    </Box>
  );
};

export const PaymentMethodsQueryString = graphql`
  query PaymentMethodsQuery {
    session {
      user {
        ... on MemberUser {
          memberProfiles(active: true) {
            edges {
              node {
                member {
                  ... on Customer {
                    club {
                      name
                    }
                    memberCards: squareCards(enabled: true) {
                      edges {
                        node {
                          ...SquareCardFragment
                          ...SelectedPaymentMethodFragment
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          squareCards(enabled: true) {
            edges {
              node {
                ...SquareCardFragment
                ...SelectedPaymentMethodFragment
              }
            }
          }
        }
      }
    }
  }
`;
